import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../../config/URL";
import fetchAllCoursesWithIds from "../List/CourseList";
import fetchAllStudentsWithIds from "../List/StudentList";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import html2canvas from "html2canvas"; // Import html2canvas
import fetchAllCentersWithIds from "../List/CenterList";
import SendAndPublish from "./SendAndPublish";
import { MdOutlineCancel } from "react-icons/md";
import { GrCurrency } from "react-icons/gr";
import { FaFilePdf } from "react-icons/fa";
import Hougang from "../../assets/clientimage/AL-Hougang-Logo.jpg";
import BukitBatok from "../../assets/clientimage/AL-BukitBatok-Logo.jpg";
import UpperTomson from "../../assets/clientimage/AL-UpperTomson-Logo.jpg";
import logo from "../../assets/images/Logo-Portal_Access.png";
import HougangQR from "../../assets/clientimage/AL-HG-QR.jpg";
import BukitBatokQR from "../../assets/clientimage/AL-BB-QR.jpg";
import UpperTomsonQR from "../../assets/clientimage/AL-UT-QR.jpg";

// Helper to safely format any value to 2 decimal places
const toDecimal = (value) => {
  const num = parseFloat(value);
  return isNaN(num) ? "0.00" : num.toFixed(2);
};

function InvoiceView() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const navigate = useNavigate();
  const [courseData, setCourseData] = useState(null);
  const [studentData, setStudentData] = useState(null);
  const [centerData, setcenterData] = useState(null);
  const [loadIndicator, setLoadIndicator] = useState(false);
  const [taxData, setTaxData] = useState([]);
  const invoiceRef = useRef(null); // Ref to capture the invoice UI

  const fetchData = async () => {
    setLoadIndicator(true);
    try {
      const courseData = await fetchAllCoursesWithIds();
      const studentData = await fetchAllStudentsWithIds();
      const centerData = await fetchAllCentersWithIds();
      setCourseData(courseData);
      setStudentData(studentData);
      setcenterData(centerData);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(error.message || "Error fetching data");
    } finally {
      setLoadIndicator(false);
    }
  };

  const fetchTaxData = async () => {
    try {
      const response = await api.get("getAllTaxSetting");
      setTaxData(response.data);
    } catch (error) {
      toast.error("Error fetching tax data:", error);
    }
  };

  const qrCodeUrl = centerData
    ? centerData.reduce((src, center) => {
        return parseInt(data.centerId) === center.id
          ? center.qrCode || "--"
          : src;
      }, "")
    : "";

  const uenNumber = centerData
    ? centerData.find((center) => parseInt(data.centerId) === center.id)
        ?.uenNumber || "--"
    : "";
  const invoiceNotes = centerData
    ? centerData.find((center) => parseInt(data.centerId) === center.id)
        ?.invoiceNotes || "--"
    : "";

  const handleGeneratePDF = async () => {
    const pdf = new jsPDF({
      orientation: "p",
      unit: "px",
      format: "a3",
    });

    const convertImageToDataURL = async (url) => {
      try {
        const response = await fetch(url, { mode: "cors" });
        if (!response.ok) {
          throw new Error(`Failed to fetch image, status: ${response.status}`);
        }
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error(`Error converting image to data URL: ${error.message}`);
        return null; // Return null if the image can't be converted
      }
    };

    const addTableToPDF = async (elementRef) => {
      const element = elementRef.current;

      try {
        element.style.visibility = "visible";
        element.style.display = "block";
        const images = element.querySelectorAll("img");
        for (const img of images) {
          if (img.src && !img.src.startsWith("data:")) {
            const dataURL = await convertImageToDataURL(img.src);
            if (dataURL) {
              img.src = dataURL; // Replace image src with data URL to avoid CORS issues
            }
          }
        }

        const canvas = await html2canvas(element, {
          scale: 2, // Higher scale for better resolution
          useCORS: true, // Allow cross-origin images
          allowTaint: false, // Prevent tainted canvas
        });

        const imgData = canvas.toDataURL("image/png");
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfPageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;

        // Calculate the scaling factor to fit the content within the PDF width
        const scaleFactor = (pdfWidth - 40) / imgWidth; // 40 for left and right margins (20 each)
        const scaledWidth = imgWidth * scaleFactor;
        const scaledHeight = imgHeight * scaleFactor;

        // Calculate the number of pages needed
        const pageHeight = pdfPageHeight - 40; // 20px margin top and bottom
        const totalPages = Math.ceil(scaledHeight / pageHeight);

        // Add content to PDF page by page
        for (let page = 0; page < totalPages; page++) {
          if (page > 0) {
            pdf.addPage();
          }

          // Calculate the Y position for the current page
          const yOffset = -(page * pageHeight);
          pdf.addImage(
            imgData,
            "PNG",
            20, // Left margin
            20 + yOffset, // Top position adjusted for the page
            scaledWidth,
            scaledHeight,
            undefined,
            "FAST", // Compression for faster rendering
          );
        }

        element.style.visibility = "visible"; // Keep visible as it's the main UI
        element.style.display = "block";
      } catch (error) {
        console.error("Error generating PDF:", error);
        toast.error("Error generating PDF");
      }
    };

    await addTableToPDF(invoiceRef);

    pdf.save(`${data.invoiceNumber}.pdf`);
  };

  const VoidInvoice = async () => {
    try {
      const response = await api.put(`/convertToVoidInvoiceByInvoiceId/${id}`, {
        headers: { "Content-Type": "application/json" },
      });
      if (response.status === 200) {
        toast.success(response.data.message);
        navigate("/invoice");
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.message || "Can't be Cancelled");
    }
  };

  const getData = async () => {
    try {
      const response = await api.get(`/getGenerateInvoiceById/${id}`);
      setData(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  const centerLogos = {
    "AL @ Hougang": Hougang,
    "AL @ Bukit Batok": BukitBatok,
    "AL @ Upper Thomson": UpperTomson,
  };

  const centerQRCodes = {
    "AL @ Hougang": HougangQR,
    "AL @ Bukit Batok": BukitBatokQR,
    "AL @ Upper Thomson": UpperTomsonQR,
  };

  const centerNames = {
    "AL @ Hougang": "Arty Learning @ HG",
    "AL @ Bukit Batok": "Arty Learning @ BB",
    "AL @ Upper Thomson": "Arty Learning @ UT",
  };

  const centerLogo = centerLogos[data.center] || logo;
  const centerQRCode = centerQRCodes[data.center] || logo;
  const centerName = centerNames[data.center] || " ";

  useEffect(() => {
    getData();
    fetchData();
    fetchTaxData();
  }, [id]);

  // Calculate the sum of Item Amount and Tax Amount
  const calculateTotals = () => {
    let totalItemAmount = 0;
    let totalTaxAmount = 0;

    if (data.invoiceItemsDtoList && data.invoiceItemsDtoList.length > 0) {
      data.invoiceItemsDtoList.forEach((item) => {
        totalItemAmount += parseFloat(item.itemAmount) || 0;
        totalTaxAmount += parseFloat(item.gstAmount) || 0;
      });
    }

    return {
      totalItemAmount: totalItemAmount.toFixed(2),
      totalTaxAmount: totalTaxAmount.toFixed(2),
      totalAmount: (totalItemAmount + totalTaxAmount).toFixed(2),
    };
  };

  const { totalItemAmount, totalTaxAmount, totalAmount } = calculateTotals();

  return (
    <div className="container-fluid mb-2 minHeight">
      <ol
        className="breadcrumb my-3"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          Invoice and Payment
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          <Link to="/invoice" className="custom-breadcrumb">
            Invoice
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          Invoice View
        </li>
      </ol>
      <div className="card">
        <div
          className="d-flex px-4 py-2 justify-content-between align-items-center mb-4 flex-wrap"
          style={{ background: "#f5f7f9" }}
        >
          <div className="d-flex align-items-center mb-2 mb-md-0">
            <div className="d-flex">
              <div className="dot active"></div>
            </div>
            <span className="ms-2 text-muted">View Invoice</span>
          </div>
          <div className="d-flex align-items-center flex-wrap gap-2">
            {data.invoiceStatus?.trim().toUpperCase() === "PENDING" && (
              <button
                type="button"
                className="btn btn-border btn-sm"
                onClick={VoidInvoice}
              >
                <MdOutlineCancel className="text-danger mb-1 me-1" />
                Void Invoice
              </button>
            )}
            {["PENDING", "PARTIALLY_PAID"].includes(
              data.invoiceStatus?.trim().toUpperCase(),
            ) && (
              <button
                type="button"
                className="btn btn-border btn-sm"
                onClick={() => {
                  navigate(
                    `/payment/add?student=${data.studentId}&invoiceId=${id}`,
                  );
                }}
              >
                <GrCurrency className="text-success mb-1 me-1" />
                Make Payment
              </button>
            )}

            {data.invoiceStatus?.trim().toUpperCase() !== "PAID" && (
              <SendAndPublish
                data={data}
                getData={getData}
                id={id}
                qr={qrCodeUrl}
                invoiceNotes={invoiceNotes}
                uenNumber={uenNumber}
              />
            )}
            <button
              onClick={handleGeneratePDF}
              className="btn btn-border btn-sm"
              disabled={loadIndicator}
            >
              {loadIndicator && (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  aria-hidden="true"
                ></span>
              )}
              <FaFilePdf className="mb-1 me-1" style={{ color: "#e9841c" }} />
              Generate PDF
            </button>
            <Link to="/invoice">
              <button type="button" className="btn btn-sm btn-border">
                Back
              </button>
            </Link>
          </div>
        </div>
        <div className="card shadow border-0 minHeight">
          <div className="container-fluid p-4" ref={invoiceRef}>
            <div className="row">
              <div className="col-12 text-center">
                <div
                  style={{
                    width: "200px",
                    height: "140px",
                    margin: "0 auto",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img src={centerLogo} className="img-fluid" alt="Logo" />
                </div>
                <p
                  className="my-2 mx-0"
                  style={{ fontSize: "12px", fontWeight: "bold" }}
                >
                  {centerName}
                </p>
                <p style={{ fontSize: "12px", margin: "2px 0" }}>
                  Tel:{" "}
                  {centerData &&
                    centerData.map((center) =>
                      parseInt(data.centerId) === center.id
                        ? center.phone || "--"
                        : "",
                    )}
                </p>
                <p style={{ fontSize: "12px", margin: "2px 0" }}>
                  Email:{" "}
                  {centerData &&
                    centerData.map((center) =>
                      parseInt(data.centerId) === center.id
                        ? center.email || "--"
                        : "",
                    )}
                </p>
              </div>
            </div>
            <div className="row mt-4">
              <div className="col-12 text-center">
                <h5
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    backgroundColor: "#e9ecef",
                    padding: "10px",
                  }}
                >
                  Invoice
                </h5>
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-6 d-flex flex-column">
                <div className="d-flex align-items-baseline mb-2">
                  <strong className="invoice-label">Invoice No</strong>
                  <span className="invoice-value">
                    : {data.invoiceNumber || "--"}
                  </span>
                </div>
                <div className="d-flex align-items-baseline mb-2">
                  <strong className="invoice-label">Student Name</strong>
                  <span className="invoice-value">
                    : {data.studentName || "--"}
                  </span>
                </div>
                <div className="d-flex align-items-baseline mb-2">
                  <strong className="invoice-label">Course Name</strong>
                  <span className="invoice-value">
                    : {data.courseName || "--"}
                  </span>
                </div>
                <div className="d-flex align-items-baseline mb-2">
                  <strong className="invoice-label">Day</strong>
                  <span className="invoice-value">
                    : {data.schedule ? data.schedule?.split("/")[2] : "--"}
                  </span>
                </div>
                <div className="d-flex align-items-baseline mb-2">
                  <strong className="invoice-label">Due Date</strong>
                  <span className="invoice-value">
                    : {data.dueDate ? data.dueDate.substring(0, 10) : "--"}
                  </span>
                </div>
              </div>
              <div className="col-6 d-flex flex-column">
                <div className="d-flex align-items-baseline mb-2">
                  <strong className="invoice-label">Date</strong>
                  <span className="invoice-value">
                    :{" "}
                    {data.invoiceDate
                      ? data.invoiceDate.substring(0, 10)
                      : "--"}
                  </span>
                </div>
                <div className="d-flex align-items-baseline mb-2">
                  <strong className="invoice-label">Student ID</strong>
                  <span className="invoice-value">
                    : {data.studentUniqueId || "--"}
                  </span>
                </div>
                <div className="d-flex align-items-baseline mb-2">
                  <strong className="invoice-label">Course ID</strong>
                  <span className="invoice-value">
                    : {data.schedule || "--"}
                  </span>
                </div>
                <div className="d-flex align-items-baseline mb-2">
                  <strong className="invoice-label">Course Time</strong>
                  <span className="invoice-value">
                    : {data.courseTime ? data.courseTime : "--"}
                  </span>
                </div>
                <div className="d-flex align-items-baseline mb-2">
                  <strong className="invoice-label">Per-lesson cost</strong>
                  <span className="invoice-value">
                    {/* ✅ Per-lesson cost formatted to 2 decimal places */}:{" "}
                    {data.perLessonCost !== undefined &&
                    data.perLessonCost !== null
                      ? toDecimal(data.perLessonCost)
                      : "--"}
                  </span>
                </div>
              </div>
            </div>
            <div className="row mt-4">
              <div className="col-12">
                <div className="table-responsive">
                  <table className="table table-responsive invoice-table">
                    <thead>
                      <tr>
                        <th>No.</th>
                        <th>Description</th>
                        <th>Tax Type</th>
                        <th>Item Amount</th>
                        <th>Tax Amount</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.invoiceItemsDtoList &&
                        data.invoiceItemsDtoList.map((invoiceItem, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>{invoiceItem.item}</td>
                            <td>
                              {taxData &&
                                taxData.map((tax) =>
                                  parseInt(invoiceItem.taxType) === tax.id
                                    ? `${tax.taxType} ${tax.rate}%` || "--"
                                    : "",
                                )}
                            </td>
                            {/* ✅ Item Amount formatted to 2 decimal places */}
                            <td>{toDecimal(invoiceItem.itemAmount)}</td>
                            {/* ✅ GST/Tax Amount formatted to 2 decimal places */}
                            <td>{toDecimal(invoiceItem.gstAmount)}</td>
                            {/* ✅ Total Amount per row formatted to 2 decimal places */}
                            <td>{toDecimal(invoiceItem.totalAmount)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="row mt-3">
              <div className="col-12">
                <div className="d-flex justify-content-end">
                  <table
                    className="table table-borderless"
                    style={{ width: "auto" }}
                  >
                    <tbody>
                      <tr>
                        <td
                          style={{
                            fontSize: "12px",
                            padding: "2px 8px",
                            textAlign: "right",
                          }}
                        >
                          <strong>Item Amount:</strong>
                        </td>
                        <td
                          style={{
                            fontSize: "12px",
                            padding: "2px 8px",
                            textAlign: "right",
                          }}
                        >
                          {/* ✅ Total Item Amount formatted to 2 decimal places */}
                          {totalItemAmount || "0.00"}
                        </td>
                      </tr>
                      <tr>
                        <td
                          style={{
                            fontSize: "12px",
                            padding: "2px 8px",
                            textAlign: "right",
                          }}
                        >
                          <strong>Tax Amount:</strong>
                        </td>
                        <td
                          style={{
                            fontSize: "12px",
                            padding: "2px 8px",
                            textAlign: "right",
                          }}
                        >
                          {/* ✅ Total Tax Amount formatted to 2 decimal places */}
                          {totalTaxAmount || "0.00"}
                        </td>
                      </tr>
                      <tr>
                        <td
                          style={{
                            fontSize: "12px",
                            padding: "2px 8px",
                            textAlign: "right",
                          }}
                        >
                          <strong>Total:</strong>
                        </td>
                        <td
                          style={{
                            fontSize: "12px",
                            padding: "2px 8px",
                            textAlign: "right",
                            fontWeight: "bold",
                          }}
                        >
                          {/* ✅ Grand Total formatted to 2 decimal places */}
                          {totalAmount || "0.00"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="row mt-4">
              <div className="col-12">
                <p
                  style={{
                    fontSize: "12px",
                    fontWeight: "bold",
                    margin: "2px 0",
                  }}
                >
                  Remarks:
                </p>
                <p
                  style={{
                    fontSize: "12px",
                    fontWeight: "bold",
                    margin: "10px 0 2px 0",
                  }}
                >
                  Notes:
                </p>
                <p
                  style={{ fontSize: "12px", margin: "2px 0", color: "blue" }}
                  dangerouslySetInnerHTML={{
                    __html: invoiceNotes
                      ? invoiceNotes.replace(/\r\n/g, "<br />")
                      : "",
                  }}
                ></p>
              </div>
            </div>
            <div className="row">
              <div className="col-12"></div>
              <div className="row">
                <div className="col-12"></div>
                <div className="qr-code-container">
                  <img src={qrCodeUrl} className="qr-code-img" alt="QR Code" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InvoiceView;
